import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from '@phosphor-icons/react';

/**
 * A muted product loop with its own pause control (WCAG 2.2.2), kept below the
 * frame so it never covers the product UI. Prerendered without autoplay: a
 * reader who asked for reduced motion, or has JavaScript off, sees the poster;
 * the loop starts on the client only when motion is welcome.
 */
export function ProductVideo({
  name,
  label,
  width,
  height,
}: {
  name: string;
  label: string;
  width: number;
  height: number;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const v = ref.current;
    if (!v || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    v.play()
      .then(() => setPlaying(true))
      .catch(() => {});
  }, []);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused)
      v.play()
        .then(() => setPlaying(true))
        .catch(() => {});
    else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="s-video">
      <video
        ref={ref}
        muted
        loop
        playsInline
        preload="metadata"
        poster={`/media/${name}-poster.webp`}
        width={width}
        height={height}
        aria-label={label}
      >
        <source src={`/media/${name}.webm`} type="video/webm" />
        <source src={`/media/${name}.mp4`} type="video/mp4" />
      </video>
      <button type="button" className="s-video-btn" onClick={toggle}>
        {playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
        {playing ? 'Pause' : 'Play'}
      </button>
    </div>
  );
}

const PHONE = '(max-width: 760px)';

/**
 * A full-width silent film (the HyperFrames renders in public/media/film-*).
 * Loads nothing until the page has finished loading and the film nears the
 * viewport, plays while at least half of it is on screen, pauses when it
 * leaves, and never autoplays for a reader who asked for reduced motion: they
 * get the poster and a Play button. On a phone the film is on the first
 * screen, so without the wait for `load` its download competes with the CSS,
 * fonts and JavaScript the page needs to appear. Phones get the 960x540
 * encode (film-<name>-960.*, about a third of the size). Source projects live
 * in videos/<name>.
 */
export function Film({ name, label, transcript }: { name: string; label: string; transcript: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const userPaused = useRef(false);

  useEffect(() => {
    const v = ref.current;
    if (!v || typeof IntersectionObserver === 'undefined') return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && v.preload === 'none') v.preload = 'auto';
        if (still || userPaused.current) return;
        if (e.intersectionRatio >= 0.5)
          v.play()
            .then(() => setPlaying(true))
            .catch(() => {});
        else if (!v.paused) {
          v.pause();
          setPlaying(false);
        }
      },
      { threshold: [0, 0.5], rootMargin: '200px 0px' },
    );
    const start = () => io.observe(v);
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
    return () => {
      window.removeEventListener('load', start);
      io.disconnect();
    };
  }, []);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      userPaused.current = false;
      v.play()
        .then(() => setPlaying(true))
        .catch(() => {});
    } else {
      userPaused.current = true;
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <figure className="s-film">
      <video
        ref={ref}
        muted
        loop
        playsInline
        preload="none"
        poster={`/media/film-${name}-poster.webp`}
        width={1920}
        height={1080}
        aria-label={label}
      >
        <source src={`/media/film-${name}-960.webm`} type="video/webm" media={PHONE} />
        <source src={`/media/film-${name}-960.mp4`} type="video/mp4" media={PHONE} />
        <source src={`/media/film-${name}.webm`} type="video/webm" />
        <source src={`/media/film-${name}.mp4`} type="video/mp4" />
      </video>
      <button type="button" className="s-film-btn" onClick={toggle} aria-label={playing ? 'Pause film' : 'Play film'}>
        {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
      </button>
      <figcaption className="sr-only">{transcript}</figcaption>
    </figure>
  );
}
